import { createContext, useContext, type ReactNode } from 'react'

/**
 * Decouples the chart's color UI from any specific picker implementation.
 *
 * The IndicatorSettingsDrawer renders its color swatches through whatever
 * component the host app injects here, falling back to a native
 * `<input type="color">` when no provider is present. This lets the frontend
 * reuse its own swatch-grid picker (which depends on shadcn/Tailwind) without
 * dragging those deps into this lean charts package.
 */

export interface ChartColorPickerProps {
  value: string
  onChange: (color: string) => void
  title?: string
  /**
   * Show the opacity slider inside the picker. Default false — the indicator
   * drawer manages opacity in a separate row, but the chart settings modal
   * needs in-picker opacity for fills (pre-market, after-hours, crosshair).
   */
  showOpacity?: boolean
}

export type ChartColorPickerComponent = (props: ChartColorPickerProps) => ReactNode

export const ChartColorPickerContext = createContext<ChartColorPickerComponent | null>(null)

export function useChartColorPicker(): ChartColorPickerComponent | null {
  return useContext(ChartColorPickerContext)
}
